import _ from 'lodash';
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { platformType } from '../../../core/utils/platform-type';

import {
  Answer,
  AnswerContainer,
  Container,
  Content,
  Divider,
  ErrorMessage,
  FooterContent,
  HeaderContent,
  Question,
  QuestionContainer,
  QuestionOptions,
  ScreenerInfo,
  TopicAnswer,
  TopicScreener,
  Title,
  UserInterviewsMessage,
  Image,
} from './styles';

const Screener = (props) => {
  const {
    screenerSession,
    title,
    segment,
    showUserInterviewsRecruited,
  } = props;

  const qualifyResult = (disqualify, qualify) => {
    if (disqualify) {
      return '(Disqualify)';
    } if (qualify) {
      return '(Qualify)';
    }
    return '(May Select)';
  };

  const getQuestionAnswer = (questions, answers, questionId) => {
    const questionAnswers = _.filter(answers, { questionId });

    return _.map(questionAnswers, (questionAnswer) => {
      const question = _.find(questions, { id: questionAnswer.questionId });
      const option = _.find(question.questionOptions, { id: questionAnswer.questionOptionId });

      return `${option.label} (${qualifyResult(option.disqualify, option.qualify).replace(/[()]/g, '')})`;
    });
  };

  const findAnswersByQuestionId = (questions, answers, questionId) => {
    const questionAnswers = _.map(
      getQuestionAnswer(questions, answers, questionId), (answer) => answer,
    );

    if (_.isEmpty(questionAnswers)) {
      return (
        <Answer>No answer</Answer>
      );
    }

    return questionAnswers.map((questionAnswer, index) => {
      const key = `${questionId}-${index}`;

      return (
        <Answer key={key}>{questionAnswer}</Answer>
      );
    });
  };

  const { screenerSessionAnswers: answers } = screenerSession || {};

  const surveyVersion = useMemo(() => {
    const foundAnswer = segment.surveys.flatMap((survey) => (
      survey.questions.flatMap((question) => (answers?.find((answer) => answer.questionId === question.id)
        ? survey.version
        : null))));

    return foundAnswer.find((version) => version !== null) || null;
  }, [segment, answers]);

  const screenerSurvey = _.find(segment?.surveys, { surveyType: 'screener', version: surveyVersion });

  if (!screenerSurvey || !screenerSession) {
    return <ErrorMessage>{'This project doesn\'t have a screener set up.'}</ErrorMessage>;
  }

  let { questions } = screenerSurvey;
  if (questions) questions = _.orderBy(questions, ['questionOrder'], 'asc');

  return (
    <>
      {title && <Title>{title}</Title>}
      {questions.map((question, questionIndex) => (
        <Container key={question.id}>
          <HeaderContent>
            <TopicScreener>
              {`Screener ${questionIndex + 1}`}
            </TopicScreener>
            <TopicAnswer>Answer</TopicAnswer>
          </HeaderContent>
          <Divider />
          <Content>
            <QuestionContainer>
              <Question>{question.label}</Question>

              {_.orderBy(question.questionOptions, ['order']).map((questionOption) => (
                <QuestionOptions key={questionOption.id}>
                  {questionOption.label}
                  {' '}
                  {qualifyResult(questionOption.disqualify, questionOption.qualify)}
                </QuestionOptions>
              ))}
            </QuestionContainer>

            <AnswerContainer>
              {findAnswersByQuestionId(questions, answers, question.id)}
            </AnswerContainer>
          </Content>

        </Container>
      ))}

      {platformType === 'company' && showUserInterviewsRecruited && (
        <UserInterviewsMessage>
          Participant recruited by
          <Image src="/images/user-interviews-logo.svg" alt="User Interviews" />
        </UserInterviewsMessage>
      )}

      <FooterContent>
        <ScreenerInfo>
          {`Completed screener version ${surveyVersion}`}
        </ScreenerInfo>
      </FooterContent>
    </>
  );
};

Screener.propTypes = {
  screenerSession: PropTypes.shape(),
  segment: PropTypes.shape().isRequired,
  title: PropTypes.string,
  showUserInterviewsRecruited: PropTypes.bool,
};

Screener.defaultProps = {
  screenerSession: null,
  title: null,
  showUserInterviewsRecruited: false,
};

export default Screener;
